// src/marketIntelLogic.js
// Shared Market Intel scoring/classification logic — used by both
// MarketIntelligence.jsx (the Reports page) and ShowMode.jsx (the Buy tab's
// Avoid alert). Kept in one place deliberately: a bug fix here only needs
// to happen once, rather than needing to be applied identically in two
// separate copies that could quietly drift apart over time.

import CONFIG from "./config";

// Market Intel data is shared across every Pro vendor, uploaded only by
// Super Admin on everyone's behalf. This is the Super Admin's own vendor ID
// (ParkerTradingCo), used as the single shared bucket every Pro vendor
// reads from. Same pattern as the demo account's fixed vendor ID used
// elsewhere in the app.
export const SHARED_MARKET_INTEL_VENDOR_ID = "37d5b868-6dfc-4fe1-adf9-fe646b5ea9c4";

// ── SCORING FORMULAS (from analytics_report.rtf) ─────────────────────────────
export function calcMomentumScore(curr, prev, totalCards) {
  if (!prev) return null;
  const rankChange = prev.rank - curr.rank; // positive = improved (moved up)
  const rankChangePct = rankChange / totalCards;
  // Price lag: rank improved more than price
  const priceDiff = curr.avg_price - prev.avg_price;
  const pricePct = prev.avg_price > 0 ? priceDiff / prev.avg_price : 0;
  const consistencyBonus = rankChange > 0 ? 10 : 0;
  const priceLagBonus = rankChangePct > 0 && pricePct < rankChangePct ? 15 : 0;
  const raw = (rankChangePct * 100 * 0.5) + (consistencyBonus * 0.3) + (priceLagBonus * 0.2);
  return Math.max(0, Math.min(100, raw + 50)); // normalize to 0-100
}

export function calcStabilityScore(history) {
  if (history.length < 2) return null;
  const ranks = history.map(h => h.rank);
  const avg = ranks.reduce((s, r) => s + r, 0) / ranks.length;
  const variance = ranks.reduce((s, r) => s + Math.pow(r - avg, 2), 0) / ranks.length;
  const avgWeight = Math.max(0, (100 - avg) / 100) * 40;
  return Math.max(0, Math.min(100, (100 - Math.sqrt(variance)) + avgWeight));
}

export function calcDivergenceScore(curr, prev) {
  if (!prev) return null;
  const totalCards = 100;
  const rankImprovePct = ((prev.rank - curr.rank) / totalCards) * 100;
  const priceIncreasePct = prev.avg_price > 0
    ? ((curr.avg_price - prev.avg_price) / prev.avg_price) * 100
    : 0;
  const raw = (rankImprovePct * 0.6) - (priceIncreasePct * 0.4);
  return Math.max(0, Math.min(100, raw + 50));
}

export function calcMasterScore(momentum, stability, divergence) {
  const m = momentum ?? 50;
  const s = stability ?? 50;
  const d = divergence ?? 50;
  return Math.round((m * 0.4) + (s * 0.35) + (d * 0.25));
}

export function scoreLabel(score) {
  if (score >= 90) return { label: "🔥 Aggressive Buy",  color: "#4ade80", tier: "A" };
  if (score >= 75) return { label: "✅ Strong Inventory", color: "#86efac", tier: "B" };
  if (score >= 60) return { label: "📦 Stable Inventory", color: "#60a5fa", tier: "C" };
  if (score >= 40) return { label: "👀 Watchlist",         color: "#fbbf24", tier: "D" };
  return             { label: "⛔ Avoid",                  color: "#f87171", tier: "F" };
}

// ─── UNIFIED OPPORTUNITY VERDICT ─────────────────────────────────────────────
// Produces BUY / HOLD / AVOID from ALL available signals — not just divergence.
// This ensures the label always matches the narrative explanation.
export function opportunityTier(divergence, c) {
  // If called without full card context (legacy path), fall back to divergence only
  if (!c) {
    if (divergence === null) return null;
    if (divergence >= 70) return { label: "BUY",  color: "#4ade80" };
    if (divergence >= 50) return { label: "HOLD", color: "#fbbf24" };
    return                       { label: "AVOID", color: "#f87171" };
  }

  const phase      = c.marketPhase || "insufficient_data";
  const phaseMeta  = PHASE_META[phase] || PHASE_META.insufficient_data;
  const pokeTier   = c.pokemonTier;
  const master     = c.master ?? 50;
  const stability  = c.stability ?? 50;
  const totalMonths = c.totalMonths ?? 1;

  // Tier 1 Pokémon in a dip-buying phase — always BUY regardless of divergence.
  // The longPricePct clause requires the card NOT be in_freefall — a large
  // long-term decline alone isn't enough, it has to have actually stopped
  // falling. Previously this caught cards still actively crashing, treating
  // "still falling" the same as "already recovered from a dip."
  const isTier1DipBuy = pokeTier?.tier === 1 &&
    (phase === "post_release_dip" || phase === "floor_forming" ||
     phase === "recovering" ||
     (phase !== "in_freefall" && c.longPricePct != null && c.longPricePct < -20));

  if (isTier1DipBuy) return { label: "BUY", color: "#4ade80" };

  // High master score with strong stability = BUY
  if (master >= 70 && stability >= 60) return { label: "BUY", color: "#4ade80" };

  // Phase override: dip/floor/recovering phases suggest buying opportunity
  if (phaseMeta.overrideAvoid && master >= 45)
    return { label: "BUY", color: "#4ade80" };

  // Rising phase with good score
  if (phase === "rising" && master >= 60) return { label: "BUY", color: "#4ade80" };

  // ── AVOID signals ───────────────────────────────────────────────────────
  // In freefall with poor master score
  if (phase === "in_freefall" && master < 45) return { label: "AVOID", color: "#f87171" };

  // Poor master score overall
  if (master < 35) return { label: "AVOID", color: "#f87171" };

  // Low divergence + rising phase = price already baked in
  if (divergence !== null && divergence < 35 && phase === "rising")
    return { label: "AVOID", color: "#f87171" };

  // ── HOLD everything else ────────────────────────────────────────────────
  return { label: "HOLD", color: "#fbbf24" };
}

// ─── POKEMON POPULARITY TIER ─────────────────────────────────────────────────
// Returns 1, 2, or null based on CONFIG.POPULAR_POKEMON
export function getPokemonTier(cardName) {
  if (!cardName) return null;
  const name = cardName.toLowerCase();
  const t1 = CONFIG.POPULAR_POKEMON.tier1.find(p => name.includes(p.toLowerCase()));
  if (t1) return { tier: 1, name: t1 };
  const t2 = CONFIG.POPULAR_POKEMON.tier2.find(p => name.includes(p.toLowerCase()));
  if (t2) return { tier: 2, name: t2 };
  return null;
}

// ─── MARKET PHASE DETECTION ──────────────────────────────────────────────────
// Analyzes the full price/rank history to determine where a card is in its cycle.
// Returns one of: rising | post_release_dip | floor_forming | recovering |
//                 in_freefall | mature_stable | insufficient_data
export function detectMarketPhase(card) {
  const history = card.history || [];
  if (history.length < 2) return "insufficient_data";

  const sorted = [...history].sort((a, b) => a.report_month.localeCompare(b.report_month));
  const prices = sorted.map(h => parseFloat(h.avg_price) || 0);
  const ranks  = sorted.map(h => parseInt(h.rank) || 999);
  const n      = prices.length;

  // Month-over-month deltas
  const priceDeltas = prices.slice(1).map((p, i) => p - prices[i]);
  const rankDeltas  = ranks.slice(1).map((r, i)  => ranks[i] - r); // positive = improved

  const latestPriceDelta = priceDeltas[priceDeltas.length - 1];
  const latestRankDelta  = rankDeltas[rankDeltas.length - 1];

  // Is price declining? Count consecutive down months from latest
  let consecutiveDown = 0;
  for (let i = priceDeltas.length - 1; i >= 0; i--) {
    if (priceDeltas[i] < -0.5) consecutiveDown++;
    else break;
  }

  // Rate of decline slowing? Compare magnitude of last delta vs previous
  const decliningSlowing = priceDeltas.length >= 2 &&
    Math.abs(latestPriceDelta) < Math.abs(priceDeltas[priceDeltas.length - 2]) * 0.6;

  // Price recovered from a prior low?
  const minPrice  = Math.min(...prices.slice(0, -1)); // min excluding latest
  const recovering = prices[n - 1] > minPrice * 1.05 && prices[n - 1] > prices[n - 2];

  // Set age in days using release date from config
  const setName  = (card.set_name || "").toLowerCase().trim();
  const setEntry = CONFIG.POKEMON_SETS.find(s => s.name?.toLowerCase() === setName);
  const daysOld  = setEntry?.released
    ? Math.floor((Date.now() - new Date(setEntry.released).getTime()) / 86400000)
    : null;
  // Post-release correction window: 0-90 days after launch
  const isNewSet = daysOld !== null && daysOld <= 90;
  // Early correction (first 6 weeks): price drops are very expected
  const isEarlyRelease = daysOld !== null && daysOld <= 42;

  // ── Phase classification ──
  if (recovering) return "recovering";

  if (consecutiveDown >= 2 && isEarlyRelease) return "post_release_dip";
  if (consecutiveDown >= 2 && isNewSet)       return "post_release_dip";

  if (consecutiveDown >= 2 && decliningSlowing) return "floor_forming";

  if (consecutiveDown >= 3 && !decliningSlowing) return "in_freefall";

  if (latestPriceDelta > 0.5 || latestRankDelta > 3) return "rising";

  if (Math.abs(latestPriceDelta) <= 0.5 && Math.abs(latestRankDelta) <= 2) return "mature_stable";

  return "insufficient_data";
}

// ─── PHASE METADATA ──────────────────────────────────────────────────────────
export const PHASE_META = {
  rising:            { label: "Rising",             emoji: "📈", overrideAvoid: false },
  post_release_dip:  { label: "Post-Release Dip",   emoji: "📉", overrideAvoid: true  },
  floor_forming:     { label: "Floor Forming",       emoji: "📊", overrideAvoid: true  },
  recovering:        { label: "Recovering",          emoji: "🔄", overrideAvoid: true  },
  in_freefall:       { label: "In Freefall",         emoji: "⚠️",  overrideAvoid: false },
  mature_stable:     { label: "Mature / Stable",     emoji: "➡️",  overrideAvoid: false },
  insufficient_data: { label: "Limited Data",        emoji: "❓", overrideAvoid: false },
};

// ─── SINGLE-CARD ANALYTICS ────────────────────────────────────────────────────
// Same computation as the full dashboard, just for one card — used by Show
// Mode's Buy tab Avoid alert. Matching is fuzzy (TCGPlayer's product names
// don't always match exactly what gets typed at a show), and only returns a
// result when there's a single, reasonably confident match — ambiguous or
// no matches return null rather than guessing.
export function computeCardAnalytics(allReports, cardName) {
  if (!cardName?.trim() || !allReports?.length) return null;

  const months = [...new Set(allReports.map(r => r.report_month))].sort();
  if (months.length === 0) return null;
  const currMonth = months[months.length - 1];
  const prevMonth = months.length > 1 ? months[months.length - 2] : null;

  const needle = cardName.trim().toLowerCase();
  const candidateNames = [...new Set(allReports.map(r => r.product_name?.trim()).filter(Boolean))];
  const exactMatch = candidateNames.find(name => name.toLowerCase() === needle);
  let matchedName;
  if (exactMatch) {
    matchedName = exactMatch;
  } else {
    const matches = candidateNames.filter(name => {
      const n = name.toLowerCase();
      return n.includes(needle) || needle.includes(n);
    });
    if (matches.length !== 1) return null;
    matchedName = matches[0];
  }

  const rawHistory = allReports.filter(r => r.product_name?.trim() === matchedName);
  const monthMap = new Map();
  for (const r of rawHistory) {
    const m = r.report_month;
    if (!monthMap.has(m) || r.rank < monthMap.get(m).rank) monthMap.set(m, r);
  }
  const history = [...monthMap.values()].sort((a, b) => a.report_month.localeCompare(b.report_month));
  if (history.length === 0) return null;

  const curr = history[history.length - 1];
  const prev = prevMonth ? history.find(h => h.report_month === prevMonth) : null;
  const totalMonths = history.length;

  const oldest = history[0];
  const hasLongHistory = totalMonths >= 3;
  const longPricePct = hasLongHistory && oldest.avg_price > 0
    ? (curr.avg_price - oldest.avg_price) / oldest.avg_price * 100
    : null;

  const currMonthReports = allReports.filter(r => r.report_month === currMonth);
  const totalCards = new Set(currMonthReports.map(r => r.product_name)).size || 100;

  const momentum   = calcMomentumScore(curr, prev, totalCards);
  const stability  = calcStabilityScore(history);
  const divergence = calcDivergenceScore(curr, prev);
  const master      = calcMasterScore(momentum, stability, divergence);
  const marketPhase = detectMarketPhase({ history, set_name: curr.set_name });
  const pokemonTier  = getPokemonTier(matchedName);

  const partial = {
    ...curr, prev, history, momentum, stability, divergence,
    master, longPricePct, totalMonths, marketPhase, pokemonTier,
  };

  return {
    ...partial,
    matchedName,
    opportunity: opportunityTier(divergence, partial),
    scoreInfo:   scoreLabel(master),
  };
}

